import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { DemoGenieCloneService } from "@/services/demo/DemoGenieCloneService";
import { CloneDemoGenieParams } from "@/types/demo-genie";
import { toast } from "sonner";
import { getDiscoverAttribution } from "@/lib/discover-attribution";
import { trackEvent } from "@/lib/ga4";

/**
 * Hook for cloning a demo genie to the user's account.
 * Returns a TanStack mutation with loading/error states.
 */
export const useCloneDemoGenie = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (params: CloneDemoGenieParams) =>
      DemoGenieCloneService.cloneToUserAccount(params),
    onSuccess: (result) => {
      trackEvent("clone_completed", {
        persona_id: result.personaId,
        agent_id: result.agent?.id,
        ...getDiscoverAttribution(),
      });
      toast.success("Your genie is ready!");
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      if (options?.onSuccess) {
        options.onSuccess();
      } else {
        navigate(`/user-hub`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create your genie");
    },
  });
};
